import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, Globe, Mail, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

interface Prospect {
  id: number;
  nombre: string;
  email: string;
  web: string | null;
  isla: string | null;
  municipio: string | null;
  tipo_fuente: string | null;
  sequence_type: 'A' | 'B' | 'C';
  status: string;
  priority: string | null;
  final_score: number | null;
  current_step: number;
  last_sent_at: string | null;
}

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'in_review', label: 'En revisión' },
  { value: 'approved', label: 'Aprobado' },
  { value: 'sent_step1', label: 'Enviado paso 1' },
  { value: 'pending_step2', label: 'Esperando paso 2' },
  { value: 'sent_step2', label: 'Enviado paso 2' },
  { value: 'pending_step3', label: 'Esperando paso 3' },
  { value: 'sent_step3', label: 'Enviado paso 3' },
  { value: 'done', label: 'Completado' },
  { value: 'replied', label: 'Respondió' },
  { value: 'interested', label: 'Interesado' },
  { value: 'meeting_booked', label: 'Reunión' },
  { value: 'skipped', label: 'Saltado' },
  { value: 'unsubscribed', label: 'Baja' },
  { value: 'bounced', label: 'Rebotado' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  in_review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  sent_step1: 'bg-teal-100 text-teal-700',
  pending_step2: 'bg-teal-50 text-teal-600',
  sent_step2: 'bg-teal-200 text-teal-800',
  pending_step3: 'bg-indigo-50 text-indigo-600',
  sent_step3: 'bg-indigo-100 text-indigo-700',
  done: 'bg-green-100 text-green-700',
  replied: 'bg-green-200 text-green-800',
  interested: 'bg-green-300 text-green-900',
  meeting_booked: 'bg-purple-100 text-purple-700',
  closed: 'bg-purple-200 text-purple-800',
  skipped: 'bg-gray-100 text-gray-400',
  unsubscribed: 'bg-red-50 text-red-500',
  bounced: 'bg-red-100 text-red-600',
};

export default function OutreachProspectsPage() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [seqFilter, setSeqFilter] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(LIMIT),
    });
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (seqFilter) params.set('seq', seqFilter);

    try {
      const res = await fetch(`/api/outreach/prospects?${params}`);
      const data = await res.json() as { prospects: Prospect[]; total: number };
      setProspects(data.prospects ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, seqFilter]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        type="outreach"
        icon={Users}
        title="Prospects"
        subtitle={`${total} total`}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Buscar por nombre, email, dominio..."
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          {STATUS_OPTS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={seqFilter}
          onChange={e => { setSeqFilter(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">Todas las secuencias</option>
          <option value="A">Seq A (Web)</option>
          <option value="B">Seq B (Booking)</option>
          <option value="C">Seq C (Sin web)</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-teal-500" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Nombre</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Isla</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Estado</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Seq</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Web</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {prospects.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 truncate max-w-48">{p.nombre}</div>
                    <div className="text-xs text-gray-400">{p.tipo_fuente}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="flex items-center gap-1 text-gray-600">
                      <Mail size={12} />
                      <span className="truncate max-w-40">{p.email}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                    {p.municipio ?? p.isla ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[p.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-xs text-gray-500">Seq {p.sequence_type}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {p.web ? (
                      <a href={p.web} target="_blank" rel="noopener"
                        className="flex items-center gap-1 text-teal-600 hover:underline text-xs truncate max-w-32">
                        <Globe size={11} />
                        {p.web.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {prospects.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              No se encontraron prospects con estos filtros
            </div>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="btn-secondary px-4 py-2 text-sm disabled:opacity-40">← Anterior</button>
          <span className="py-2 px-3 text-sm text-gray-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            className="btn-secondary px-4 py-2 text-sm disabled:opacity-40">Siguiente →</button>
        </div>
      )}
    </div>
  );
}
